import { AnamEvent, createClient, type AnamClient } from "@anam-ai/js-sdk";
import type { SessionStart } from "./api";

export interface AvatarHandlers {
  /** Streamed transcript: same id may arrive many times with growing text. */
  onMessage: (id: string, role: "user" | "persona", text: string) => void;
  onUserSpeech: (state: "started" | "ended") => void;
  /** First persona text of a turn (reply started). */
  onAgentReply: () => void;
  onSessionReady: (anamSessionId: string) => void;
  /** `played` is false when the browser blocked autoplay with sound (show a tap-to-start overlay). */
  onVideoPlaying: (played: boolean) => void;
  onClosed: (reason: string, details?: string) => void;
  onWarning: (message: string) => void;
  /** Client tool calls from the Anam persona. Return the tool result string. */
  onShowMedia: (mediaId: string) => Promise<string>;
  onHideMedia: () => Promise<string>;
  onGetScreenState: () => Promise<string>;
  onDescribeMedia: (mediaId: string) => Promise<string>;
}

const START_TIMEOUT_MS = 20_000;

/**
 * Anam-only session: the persona is the agent (STT → LLM → TTS + tools) and renders the face. The browser holds one
 * Anam WebRTC connection; we hand it our own mic stream and attach its media to our own <video>, because
 * streamToVideoElement(id) can't see into a closed shadow root.
 */
export class AvatarSession {
  private anam: AnamClient | undefined;
  private personaTurnOpen = false;
  private video: HTMLVideoElement | undefined;

  constructor(private readonly handlers: AvatarHandlers) {}

  async start(s: SessionStart, video: HTMLVideoElement, mic: MediaStream): Promise<void> {
    const h = this.handlers;
    const anam = createClient(s.sessionToken);
    this.anam = anam;

    anam.registerToolCallHandler("show_media", { onStart: async (p) => h.onShowMedia(String(p.arguments.media_id ?? "")) });
    anam.registerToolCallHandler("hide_media", { onStart: async () => h.onHideMedia() });
    anam.registerToolCallHandler("get_screen_state", { onStart: async () => h.onGetScreenState() });
    anam.registerToolCallHandler("describe_media", { onStart: async (p) => h.onDescribeMedia(String(p.arguments.media_id ?? "")) });

    anam.addListener(AnamEvent.SESSION_READY, (id) => h.onSessionReady(id));
    anam.addListener(AnamEvent.CONNECTION_CLOSED, (reason, details) => h.onClosed(String(reason), details));
    anam.addListener(AnamEvent.SERVER_WARNING, h.onWarning);
    anam.addListener(AnamEvent.USER_SPEECH_STARTED, () => {
      this.personaTurnOpen = false;
      h.onUserSpeech("started");
    });
    anam.addListener(AnamEvent.USER_SPEECH_ENDED, () => h.onUserSpeech("ended"));
    anam.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (m) => {
      if (m.role === "persona" && !this.personaTurnOpen) {
        this.personaTurnOpen = true;
        h.onAgentReply();
      }
      if (m.role === "persona" && m.endOfSpeech) this.personaTurnOpen = false;
      h.onMessage(m.id, m.role === "user" ? "user" : "persona", m.content);
    });

    const streams = await withTimeout(anam.stream(mic), START_TIMEOUT_MS, "avatar_start_timeout");
    this.video = video;
    video.srcObject = new MediaStream(streams.flatMap((m) => m.getTracks()));
    video.muted = false;
    // Usually allowed (the flow began with a user tap); iOS may still refuse sound after the awaits — the widget then shows a tap-to-start overlay.
    const played = await video.play().then(() => true, () => false);
    h.onVideoPlaying(played);
  }

  /** Called from a user gesture when autoplay with sound was refused. */
  async resumeVideo(): Promise<boolean> {
    const v = this.video;
    if (!v) return false;
    v.muted = false;
    return v.play().then(() => true, () => false);
  }

  /** Typed input: treated as if the visitor said it. */
  sendText(text: string): void {
    this.anam?.sendUserMessage(text);
  }

  /** Non-turn-triggering context injection ("Screen update: …"), used on the next persona turn. */
  sendContext(text: string): void {
    this.anam?.addContext(text);
  }

  setMuted(muted: boolean): void {
    if (muted) this.anam?.muteInputAudio();
    else this.anam?.unmuteInputAudio();
  }

  anamSessionId(): string | undefined {
    return this.anam?.getActiveSessionId() ?? undefined;
  }

  async stop(): Promise<void> {
    const anam = this.anam;
    this.anam = undefined;
    if (anam?.isStreaming()) await anam.stopStreaming().catch(() => undefined);
  }
}

function withTimeout<T>(p: Promise<T>, ms: number, code: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(code)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}
